import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { X, ArrowRight, CheckCircle2 as CheckCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function InterviewModal({ brandId, onClose }) {
  const [session, setSession] = useState(null);
  const [answer, setAnswer] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const taRef = useRef(null);

  useEffect(() => {
    api.startInterview(brandId).then((s) => {
      setSession(s);
      if (s.completed) {
        setDone(true);
      } else if (!s.current_question || s.next_pending) {
        // resumed mid-generation — kick off polling
        startPolling(s.session_id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  useEffect(() => { taRef.current?.focus(); }, [session?.current_question]);

  const startPolling = (sid) => {
    setSubmitting(true);
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const s = await api.getInterview(sid);
        if (s.completed) {
          setDone(true);
          setSubmitting(false);
          return;
        }
        if (s.current_question && !s.next_pending) {
          setSession(s);
          setSubmitting(false);
          return;
        }
        if (s.next_error) {
          setSubmitting(false);
          return;
        }
      } catch {/* ignore */}
      if (attempts < 90) setTimeout(poll, 2000);
      else setSubmitting(false);
    };
    setTimeout(poll, 1500);
  };

  const submit = async () => {
    if (!answer.trim() || !session) return;
    setSubmitting(true);
    try {
      const res = await api.answerInterview(session.session_id, answer);
      setAnswer("");
      if (res.completed) {
        setDone(true);
        setSubmitting(false);
        return;
      }
      if (res.pending) {
        // optimistic: clear current question and start polling
        setSession((prev) => prev ? { ...prev, question_count: res.question_count, current_question: null, next_pending: true } : prev);
        startPolling(session.session_id);
      } else {
        setSession({ ...session, question_count: res.question_count, current_question: res.next_question });
        setSubmitting(false);
      }
    } catch (e) {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="interview-modal">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border-2 border-black w-full max-w-3xl brutal-shadow-lg"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-black bg-[#F4F5F6]">
          <div className="flex items-center gap-3">
            <span className="text-eyebrow">Iterative Interview</span>
            <span className="mono text-sm">{(session?.question_count ?? 0)}/{session?.questions_per_session ?? 20}</span>
            {session && !done && (
              <button
                onClick={async () => {
                  if (!window.confirm("Start a fresh 20-question session? Your previous answers stay saved as brand knowledge.")) return;
                  const s = await api.startInterview(brandId, true);
                  setSession(s); setDone(false); setAnswer("");
                  if (!s.current_question || s.next_pending) startPolling(s.session_id);
                }}
                data-testid="start-fresh-button"
                className="text-eyebrow text-[#A1A1AA] hover:text-[#E11D48] ml-2"
              >
                Start fresh
              </button>
            )}
          </div>
          <button onClick={onClose} data-testid="close-interview"><X size={20} /></button>
        </div>

        <div className="p-10 min-h-[360px]">
          {!session ? (
            <div className="text-eyebrow">Preparing first question…</div>
          ) : done ? (
            <div className="text-center py-12">
              <CheckCircle size={64} className="mx-auto text-[#059669]" />
              <h2 className="font-display font-black text-3xl mt-4 tracking-tighter">Session complete.</h2>
              <p className="text-[#52525B] mt-2">Brand knowledge updated. Start another session or generate content.</p>
              <button onClick={onClose} className="mt-6 bg-[#0A0A0B] text-white px-6 py-3 text-sm">Close</button>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={session.current_question || `loading-${session.question_count}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.3 }}
              >
                <div className="text-eyebrow mb-4">Question {session.question_count + 1}</div>
                {session.current_question ? (
                  <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight leading-snug" data-testid="interview-question">
                    {session.current_question}
                  </h3>
                ) : (
                  <h3 className="font-display font-bold text-2xl tracking-tight leading-snug text-[#A1A1AA]" data-testid="interview-question-loading">
                    <span className="inline-block w-3 h-3 bg-[#0A0A0B] mr-2 animate-pulse" /> Thinking of the next question… <span className="text-sm font-normal text-[#A1A1AA]">(can take up to ~60 seconds in production)</span>
                  </h3>
                )}
                <textarea
                  ref={taRef}
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.metaKey && e.key === "Enter") submit();
                  }}
                  rows={5}
                  disabled={!session.current_question || submitting}
                  placeholder="Type freely. Specifics > generic. ⌘+Enter to send."
                  data-testid="interview-answer-input"
                  className="mt-6 w-full border-2 border-[#E2E4E9] focus:border-black p-4 outline-none resize-none text-base disabled:bg-[#F4F5F6]"
                />
                <div className="mt-4 flex items-center justify-end gap-3">
                  <button onClick={onClose} className="px-5 py-3 text-sm border border-black hover:bg-[#F4F5F6]" data-testid="pause-interview">Pause</button>
                  <button
                    onClick={submit}
                    disabled={submitting || !answer.trim()}
                    data-testid="next-question-button"
                    className="px-5 py-3 text-sm bg-[#0A0A0B] text-white hover:bg-[#002FA7] disabled:opacity-50 flex items-center gap-2"
                  >
                    {submitting ? "Thinking…" : "Next"} <ArrowRight size={14} />
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </motion.div>
    </div>
  );
}
